// @flow

/**
 * StandardMasonry is built to utilize all the new Masonry9 work with the same
 * API as the former versions of Masonry. It should be a drop-in replacement for
 * existing Masonry users.
 */

import * as React from 'react';
import Layout from 'app/common/react/components/Masonry9/Layout';
import Masonry from 'app/common/react/components/Masonry9/Masonry';
import StandardLayoutEngine from 'app/common/react/components/Masonry9/StandardLayoutEngine';
import UniformLayoutEngine from 'app/common/react/components/Masonry9/UniformLayoutEngine';
import { ScrollTracker } from 'app/common/react/components/Masonry9/ScrollTracker';
import { type LayoutEngine } from 'app/common/react/components/Masonry9/LayoutEngine';
import { type renderItemProps } from 'app/common/react/components/Masonry9/Collection';
import FetchMoreScrollTracker, {
  type getFetchOverscanProps,
} from 'app/common/react/components/Masonry9/FetchMoreScrollTracker';
import {
  type getVirtualOverscanProps,
  type getItemsToMeasureLimitProps,
} from 'app/common/react/components/Masonry9/LayoutRenderer';
import { memoize, memoizeOne } from 'app/packages/memoize/memoize';

type MeasurementStore<ItemType> = { layout: Layout<ItemType> };

type LayoutOptions = {|
  minCols: number,
  columnWidth?: number,
  gutterWidth?: number,
  flexible?: boolean,
  layout?: 'default' | 'enhanced' | 'uniformRow',
|};

type Props<ItemType> = {|
  ...LayoutOptions,
  comp: React.ComponentType<{
    data: ItemType,
    itemIdx: number,
    isMeasuring: boolean,
  }>,
  getItemLayoutOptions?: (item: ItemType) => ?Object,
  fetchBoundsOffset: number,
  items: Array<ItemType>,
  measurementStore?: MeasurementStore<ItemType>,
  loadItems?:
    | false
    | ((
        ?{
          from: number,
        },
      ) => void | boolean | {}),
  scrollContainer?: HTMLElement | (() => ?HTMLElement),
  virtualize: boolean,
|};

type State<ItemType> = {|
  initialItemCount: number,
  layoutEngine: LayoutEngine<ItemType>,
  measurementStore: MeasurementStore<ItemType>,
  nonce: number,
|};

// Define a function factory that determines fetch bounds for the sake of
// experimentation.
const FETCH_OVERSCAN_FACTOR = 2;
const makeFetchOverscanFunction = memoize(
  (offset: number) => ({ containerSize }: getFetchOverscanProps) =>
    Math.max(0, containerSize.height * FETCH_OVERSCAN_FACTOR + offset),
);

// Virtualization bounds are defined by the screen height multiplied by this
// factor.
const VIRTUAL_OVERSCAN_FACTOR = 0.7;
const getVirtualOverscan = <ItemType>({ scrollContainerSize }: getVirtualOverscanProps<ItemType>) =>
  scrollContainerSize.height * VIRTUAL_OVERSCAN_FACTOR;

// scrollContainer can be specified as either an HTMLElement or a function that
// returns an HTMLElement. getScrollContainer manages the difference and returns
// either the final HTMLElement or null if none is present.
const getScrollContainer = (nodeOrFunc: ?(HTMLElement | (() => ?HTMLElement))) => {
  if (nodeOrFunc) {
    return typeof nodeOrFunc === 'function' ? nodeOrFunc() : nodeOrFunc;
  }

  if (typeof window !== 'undefined') {
    return window;
  }

  return null;
};

export default class StandardMasonry<ItemType> extends React.PureComponent<
  Props<ItemType>,
  State<ItemType>,
> {
  static createMeasurementStore = <T>(): {
    layout: Layout<T>,
  } => ({ layout: new Layout() });

  static defaultProps: $Shape<Props<ItemType>> = {
    columnWidth: 236,
    fetchBoundsOffset: 0,
    layout: 'default',
    minCols: 3,
    virtualize: false,
  };

  // A LayoutEngine object is an immutable object derived purely from the layout options, which
  // do not change often. When the layout options do change, we create a brand new object.
  // Note that we memoize using the layout options so that we use the same layout engine object for
  // the same layout options.
  static layoutOptionsToLayoutEngine: (LayoutOptions) => LayoutEngine<ItemType> = memoize(
    ({ layout, minCols, columnWidth, gutterWidth, flexible }) => {
      const Engine = layout === 'uniformRow' ? UniformLayoutEngine : StandardLayoutEngine;
      return new Engine({
        minCols,
        colWidth: columnWidth,
        gutter: gutterWidth,
        flexible,
      });
    },
  );

  static getDerivedStateFromProps({
    layout,
    minCols,
    columnWidth,
    gutterWidth,
    flexible,
  }: Props<ItemType>) {
    // The layoutEngine state is an immutable object derived from the layout options in the props.
    const layoutEngine: LayoutEngine<ItemType> = StandardMasonry.layoutOptionsToLayoutEngine({
      layout,
      minCols,
      columnWidth,
      gutterWidth,
      flexible,
    });
    return { layoutEngine };
  }

  constructor(props: Props<ItemType>) {
    super(props);

    const { minCols, columnWidth, gutterWidth, flexible, layout, measurementStore, items } = props;

    const localMeasurementStore = measurementStore || StandardMasonry.createMeasurementStore();

    const layoutEngine: LayoutEngine<ItemType> = StandardMasonry.layoutOptionsToLayoutEngine({
      layout,
      minCols,
      columnWidth,
      gutterWidth,
      flexible,
    });

    // If we're given a layout cache and a new items array, we want to update
    // that cache to use the new items array. Note that we're mutating the prop
    // we're given here -- measurementStore is intentially passed by reference
    // under the assumption that it will be mutated.
    localMeasurementStore.layout = new Layout(
      items,
      localMeasurementStore.layout.getItemLayoutInfo,
    );

    this.state = {
      nonce: 0,
      layoutEngine,
      measurementStore: localMeasurementStore,
      initialItemCount: items.length,
    };
  }

  getScrollTracker: (
    loadItems?:
      | false
      | ((
          ?{
            from: number,
          },
        ) => void | boolean | {}),
    fetchBoundsOffset: number,
  ) => ScrollTracker<ItemType> | typeof undefined = memoizeOne((loadItems, fetchBoundsOffset) => {
    const fetchOverscanFunc = makeFetchOverscanFunction(fetchBoundsOffset);
    return typeof loadItems === 'function'
      ? new FetchMoreScrollTracker(this.fetchMore, fetchOverscanFunc)
      : undefined;
  });

  getItemsToMeasureLimit = <ItemType>({
    contentContainerSize,
  }: getItemsToMeasureLimitProps<ItemType>) => {
    const { items } = this.props;
    const { layoutEngine, initialItemCount } = this.state;

    // If we're loading the first page, we should measure them all. Normally,
    // we'd still  want to limit the measurement, but SSR does some pre-layout.
    if (items.length === initialItemCount) {
      return Infinity;
    }

    // $FlowFixMe - We're assuming a MultiColumnLayoutEngine here.
    return layoutEngine.getColumnInfo(contentContainerSize).count;
  };

  masonryRef: { current: null | Masonry<ItemType> } = React.createRef();

  fetchMore = () => {
    const fetchPromise: Promise<boolean> = new Promise((resolve) => {
      const { items, loadItems } = this.props;

      if (typeof loadItems === 'function') {
        // Call to load new items. We do this asyncronously in case loadItems is
        // sync. It's important to let FetchMoreScrollTracker resolve its
        // promise before new props are passed into Masonry.
        setTimeout(() => {
          loadItems({ from: items.length });

          // TODO: Right now we're assuming we can fetch more in the future.
          // This assumption is incorrect and should be tied to loadItems.
          resolve(true);
        });
      } else {
        resolve(false);
      }
    });

    return fetchPromise;
  };

  handleLayoutUpdated: (layout: Layout<ItemType>) => void = (layout) => {
    const { measurementStore } = this.state;

    // If the parent passed a measurement store into Masonry, update it with the
    // latest measurement data. This is a bit of a hack and should really be
    // done by exposing the onLayoutUpdated method, but it's done this way to
    // match how the former Masonry worked.
    measurementStore.layout = layout;
  };

  updatePosition() {
    if (this.masonryRef.current) {
      this.masonryRef.current.updatePosition();
    }
  }

  reflow() {
    const { measurementStore } = this.state;

    // Clear the measurement store.
    measurementStore.layout = new Layout();

    // Update the nonce to trigger an update to keys resulting in a fresh
    // re-render.
    this.setState(({ nonce }) => ({
      nonce: nonce + 1,
    }));
  }

  renderItem: (props: renderItemProps<ItemType>) => React.Node = ({
    item,
    index,
    isMeasuring,
    constraints,
  }) => {
    const { comp: Comp } = this.props;
    return <Comp data={item} itemIdx={index} isMeasuring={isMeasuring} constraints={constraints} />;
  };

  render() {
    const {
      fetchBoundsOffset,
      getItemLayoutOptions,
      items,
      loadItems,
      scrollContainer,
      virtualize,
    } = this.props;
    const { layoutEngine, measurementStore, nonce } = this.state;

    const scrollTracker = this.getScrollTracker(loadItems, fetchBoundsOffset);

    return (
      <Masonry
        key={`Masonry-${nonce}`}
        ref={this.masonryRef}
        items={items}
        renderItem={this.renderItem}
        getItemLayoutOptions={getItemLayoutOptions}
        layoutEngine={layoutEngine}
        scrollContainer={getScrollContainer(scrollContainer)}
        scrollTracker={scrollTracker}
        onLayoutUpdated={this.handleLayoutUpdated}
        initialLayout={measurementStore.layout}
        virtualize={virtualize}
        virtualOverscan={getVirtualOverscan}
        itemsToMeasureLimit={this.getItemsToMeasureLimit}
      />
    );
  }
}
